// The single chokepoint over @gi-nx/iframe-sdk. Every quirk is documented here
// once, and nothing outside this file calls `rpc` directly.

import { giraffeState, rpc } from '@gi-nx/iframe-sdk';

/**
 * `rpc.invoke(method, params[])` splats `params` POSITIONALLY into the target
 * method — it is NOT an options bag. `rpc.invoke('fitBounds', [bounds, opts])`
 * becomes `map.fitBounds(bounds, opts)`; `flyTo` takes a single options object.
 *
 * An rpc for a method the host does not implement NEVER REJECTS. The underlying
 * mini-iframe-rpc default timeout is 10 minutes, so a `catch`-based fallback
 * silently never runs and the UI hangs with nothing said. Every call races an
 * explicit timeout so "we could not do it" is a first-class outcome.
 */
export async function invoke<T = unknown>(
  method: string,
  params: unknown[] = [],
  timeoutMs = 8000
): Promise<{ ok: true; value: T } | { ok: false; reason: string }> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const value = (await Promise.race([
      rpc.invoke(method, params),
      new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new Error(`timeout after ${timeoutMs}ms`)), timeoutMs);
      }),
    ])) as T;
    return { ok: true, value };
  } catch (err) {
    const reason = err instanceof Error ? err.message : String(err);
    // Logged as well as returned: a caller can choose to show a short message,
    // but the console needs the whole thing to be debuggable from a screenshot.
    console.warn(`[gtables] rpc ${method} failed`, { params, reason, err });
    return { ok: false, reason };
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/** True when we are inside a Giraffe iframe at all. */
export function inGiraffe(): boolean {
  try {
    return typeof window !== 'undefined' && window.parent !== window;
  } catch {
    return false;
  }
}

export function startListening(): void {
  giraffeState.listen();
  exposeForDebugging();
}

/**
 * `window.__gtables.state('layerTree')` from the console.
 *
 * This app runs in an iframe over a host whose state is only reachable through
 * the SDK — so when Giraffe sends a shape we did not expect (`layerTree` is
 * undocumented and has already changed once), there is otherwise no way to see
 * what actually arrived short of rebuilding with a console.log in it. Reads
 * only, and nothing in the app depends on it.
 */
function exposeForDebugging(): void {
  if (typeof window === 'undefined') return;
  (window as unknown as Record<string, unknown>).__gtables = {
    state: (key: string) => readUntypedState(key),
    dump: (key: string) => JSON.stringify(readUntypedState(key), null, 2),
  };
}

export type StateKey = Parameters<typeof giraffeState.addListener>[0][number];

export function onState(keys: StateKey[], cb: (key: StateKey) => void): () => void {
  const id = giraffeState.addListener(keys, (key) => cb(key));
  return () => giraffeState.removeListener(id);
}

export function readState<T = unknown>(key: StateKey): T | undefined {
  try {
    return giraffeState.get(key) as T | undefined;
  } catch {
    return undefined;
  }
}

/** Untyped state keys the SDK's union does not declare (e.g. `lensSelection`). */
export function readUntypedState<T = unknown>(key: string): T | undefined {
  try {
    return (giraffeState as unknown as { get(k: string): unknown }).get(key) as T | undefined;
  } catch {
    return undefined;
  }
}

// ---------------------------------------------------------------------------
// Writes
// ---------------------------------------------------------------------------

/**
 * Patch section properties.
 *
 * The partials must be MINIMAL — `{ properties: { id, ...patch } }`. Spreading a
 * whole baked feature fails to match and the write silently does nothing.
 * Batches are chunked because a large single call is rejected wholesale.
 */
export async function updateRawSections(
  patches: { properties: Record<string, unknown> }[],
  chunkSize = 100
): Promise<{ ok: boolean; written: number; reason?: string }> {
  let written = 0;
  for (let i = 0; i < patches.length; i += chunkSize) {
    const chunk = patches.slice(i, i + chunkSize);
    const res = await invoke('updateRawSections', [chunk], 20000);
    if (!res.ok) return { ok: false, written, reason: res.reason };
    written += chunk.length;
  }
  return { ok: true, written };
}

/**
 * `updateUsage(name, partial)` merges WITHIN `default`/`join`, so a single-field
 * write preserves every other rent and cost. `null` deletes a field. This is the
 * opposite of `updateBlock`, which replaces `properties` wholesale — the two are
 * easy to get backwards.
 *
 * Properties must go inside `default` or `join`; top-level fields are ignored.
 */
export async function updateUsage(
  name: string,
  patch: { default?: Record<string, unknown>; join?: Record<string, unknown> }
): Promise<{ ok: boolean; reason?: string }> {
  const res = await invoke('updateUsage', [name, patch], 15000);
  return res.ok ? { ok: true } : { ok: false, reason: res.reason };
}

/**
 * Delete a usage.
 *
 * `rpc.invoke('deleteUsage', [name])` — the shape `~/Documents/code/chat` uses
 * in production (`src/tools.ts`, the deleteUsages tool). It is not guessed.
 *
 * It briefly probed three argument shapes on the theory that the SDK might want
 * a different one. That was speculation piled on a call that was already right,
 * and the wrong shapes were actively harmful: passing `{ name }` hands the host
 * an object where it expects a string, which is how a bridge ends up trying to
 * `JSON.parse("[object Object]")`. When a call already has a verified shape,
 * probing for others can only do damage.
 */
export async function deleteUsage(name: string): Promise<{ ok: boolean; reason?: string }> {
  // Generous: a write to the project is worth waiting for, and abandoning one
  // that is merely slow reports a failure for something that then succeeds.
  const res = await invoke('deleteUsage', [name], 30000);
  if (res.ok) return { ok: true };
  return {
    ok: false,
    reason: isMissingProcedure(res.reason)
      ? 'This Giraffe build does not offer deleteUsage over the SDK. Remove the usage in Giraffe\u2019s own Usage Editor; everything else on this sheet still pushes.'
      : summarise(res.reason),
  };
}

/** The host does not have this method at all — a different thing from refusing it. */
function isMissingProcedure(reason: string): boolean {
  return /ProcedureNotFound|not registered/i.test(reason);
}

/** One readable line, not a stack trace. The console keeps the whole thing. */
function summarise(reason: string): string {
  const first = reason.split('\n')[0]!.trim();
  return first.length > 140 ? `${first.slice(0, 140)}\u2026` : first;
}

/**
 * Create or update usages.
 *
 * NOT the `updateUsage` RPC. Usages live in the core Giraffe app's own state,
 * at `projectAppsByAppID["1"].featureCategories.usage`, and
 * `~/Documents/code/chat` writes them there in production
 * (`src/tools.ts`, updateUsages) — read the app, merge, write it back with
 * `updateProjectApp`. It is the same call for creating and for editing, which
 * is why there is no createUsage here.
 *
 * The merge is per-usage and WITHIN `join` and `default`, so writing one field
 * keeps every other rate on that usage.
 */
export async function writeUsages(
  patches: Record<string, { join?: Record<string, unknown>; default?: Record<string, unknown> }>
): Promise<{ ok: boolean; reason?: string }> {
  const apps = readState<Record<string, unknown>>('projectAppsByAppID');
  const core = apps?.['1'] as Record<string, unknown> | undefined;
  if (!core) return { ok: false, reason: 'The core Giraffe app state is not readable yet.' };

  // A deep clone, never the live object: the SDK rejects a mutated one.
  const app = JSON.parse(JSON.stringify(core)) as Record<string, unknown>;
  const categories = (app.featureCategories ??= {}) as Record<string, unknown>;
  const usages = (categories.usage ??= {}) as Record<string, { join?: unknown; default?: unknown }>;

  for (const [name, patch] of Object.entries(patches)) {
    const existing = usages[name] ?? {};
    usages[name] = {
      ...existing,
      join: { ...((existing.join as object) ?? {}), ...(patch.join ?? {}) },
      default: { ...((existing.default as object) ?? {}), ...(patch.default ?? {}) },
    };
  }

  const res = await invoke('updateProjectApp', ['1', app], 30000);
  return res.ok ? { ok: true } : { ok: false, reason: summarise(res.reason) };
}

/**
 * Usages read straight off broadcast state, with no round trip.
 *
 * They live in the core app at `projectAppsByAppID["1"].featureCategories.usage`
 * — the same place `writeUsages` writes them — and that key IS reactive. So an
 * edit made anywhere in Giraffe reaches the sheet on the broadcast rather than
 * waiting for something to think to ask again.
 *
 * `null` means the app state is not readable yet, which is not the same as a
 * project with no usages: an existing app with no usage map has none, and `{}`
 * says so. Returning `{}` for both would blank the table during the handshake.
 */
/**
 * Remove a field from every usage in the project.
 *
 * Deleting a column on the Usages sheet is not a sheet edit — the sheet is a
 * view of the project, and the column IS the field. Same route as the writes:
 * read the core app, drop the key from every usage's `join` and `default`,
 * write the whole app back.
 */
export async function removeUsageFields(fields: readonly string[]): Promise<{ ok: boolean; reason?: string }> {
  const apps = readState<Record<string, unknown>>('projectAppsByAppID');
  const core = apps?.['1'] as Record<string, unknown> | undefined;
  if (!core) return { ok: false, reason: 'The core Giraffe app state is not readable yet.' };

  const app = JSON.parse(JSON.stringify(core)) as Record<string, unknown>;
  const categories = (app.featureCategories ?? {}) as Record<string, unknown>;
  const usages = (categories.usage ?? {}) as Record<string, { join?: Record<string, unknown>; default?: Record<string, unknown> }>;
  for (const usage of Object.values(usages)) {
    for (const field of fields) {
      if (usage.join) delete usage.join[field];
      if (usage.default) delete usage.default[field];
    }
  }

  const res = await invoke('updateProjectApp', ['1', app], 30000);
  return res.ok ? { ok: true } : { ok: false, reason: summarise(res.reason) };
}

export function readProjectUsages(): Record<string, unknown> | null {
  const apps = readState<Record<string, unknown>>('projectAppsByAppID');
  const core = apps?.['1'] as Record<string, unknown> | undefined;
  if (!core) return null;
  const categories = core.featureCategories as Record<string, unknown> | undefined;
  const usages = categories?.usage;
  if (usages != null && typeof usages === 'object') return usages as Record<string, unknown>;
  return {};
}

export async function getProjectUsages(): Promise<Record<string, unknown> | null> {
  const res = await invoke<Record<string, unknown>>('getProjectUsages', [], 10000);
  return res.ok ? res.value : null;
}

/** Select features on the map by id. */
export async function setSelectedFeatures(ids: string[]): Promise<void> {
  await invoke('setSelectedFeatures', [ids], 5000);
}

export async function setHighlightedFeatures(ids: string[]): Promise<void> {
  await invoke('setHighlightedFeatures', [ids], 5000);
}

/** `flyTo` is Mapbox's single-options-object form. Passing positional args no-ops. */
export async function flyTo(options: {
  center: [number, number];
  zoom?: number;
  pitch?: number;
  bearing?: number;
  duration?: number;
}): Promise<void> {
  await invoke('flyTo', [options], 5000);
}

export async function fitBounds(
  bounds: [[number, number], [number, number]],
  options: Record<string, unknown> = { padding: 60, duration: 600 }
): Promise<void> {
  await invoke('fitBounds', [bounds, options], 5000);
}

// ---------------------------------------------------------------------------
// Persistence primitives (used by giraffe/persist.ts)
// ---------------------------------------------------------------------------

export async function updateProjectApp(appId: string, app: unknown): Promise<{ ok: boolean; reason?: string }> {
  const res = await invoke('updateProjectApp', [appId, app], 15000);
  return res.ok ? { ok: true } : { ok: false, reason: res.reason };
}

export async function uploadProjectAttachment(payload: unknown[]): Promise<{ ok: boolean; value?: unknown; reason?: string }> {
  const res = await invoke('uploadProjectAttachment', payload, 30000);
  return res.ok ? { ok: true, value: res.value } : { ok: false, reason: res.reason };
}

/** `getTempUrl` takes a bare string in some SDK builds and an array in others. */
export async function getTempUrl(attachmentId: string): Promise<string | null> {
  for (const params of [[attachmentId], [[attachmentId]]]) {
    const res = await invoke<unknown>('getTempUrl', params, 10000);
    if (!res.ok) continue;
    const v = res.value as string | { url?: string; tempUrl?: string } | undefined;
    if (typeof v === 'string' && v) return v;
    if (v && typeof v === 'object') {
      const url = v.url ?? v.tempUrl;
      if (typeof url === 'string' && url) return url;
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Features and blocks
// ---------------------------------------------------------------------------

/** Features per `updateRawSections` call. A whole-project write is not one payload. */
const FEATURE_CHUNK = 200;

/**
 * Write properties onto features.
 *
 * MINIMAL partials — the id plus the changed fields, nothing else. Spreading a
 * whole feature (its geometry, its baked `_projected`/`_height`, or an id that
 * lives only at the top level) makes the host fail to match it and the write
 * silently does nothing. Chunked, because a whole-project retag is otherwise
 * one oversized call that fails without saying so.
 */
export async function writeFeatureProps(
  patches: Record<string, Record<string, unknown>>
): Promise<{ ok: boolean; reason?: string; written: number }> {
  const ids = Object.keys(patches);
  let written = 0;
  for (let i = 0; i < ids.length; i += FEATURE_CHUNK) {
    const updates = ids.slice(i, i + FEATURE_CHUNK).map((id) => ({ properties: { id, ...patches[id] } }));
    const res = await invoke('updateRawSections', [updates], 30000);
    if (!res.ok) return { ok: false, reason: summarise(res.reason), written };
    written += updates.length;
  }
  return { ok: true, written };
}

export interface BlockDef {
  id: string;
  name?: string;
  properties?: Record<string, unknown>;
}

/**
 * Every block definition, keyed by id.
 *
 * The `blocks` state key is documented as a Record but `getProjectBundle`
 * returns an ARRAY — and on an array `blocks[someId]` is `undefined`, so every
 * definition would come up empty. Accept both shapes, and trust a definition's
 * own id over the key it arrived under.
 */
export function readBlocks(): Record<string, BlockDef> {
  const value = readUntypedState('blocks');
  const out: Record<string, BlockDef> = {};
  if (!value || typeof value !== 'object') return out;
  if (Array.isArray(value)) {
    for (const b of value as BlockDef[]) if (b && typeof b.id === 'string') out[b.id] = b;
    return out;
  }
  for (const [key, b] of Object.entries(value as Record<string, BlockDef>)) {
    if (!b || typeof b !== 'object') continue;
    out[typeof b.id === 'string' && b.id ? b.id : key] = b;
  }
  return out;
}

/**
 * Property values on a block DEFINITION — a standard rent, a status, an area.
 *
 * On the TYPE, so every placement carries it: a standard rent is a fact about a
 * floorplan, not about the one on level 6.
 *
 * Written RAW, at the top level of `properties`, not under a namespace of this
 * app's own. The whole point of putting a value on the block is that Giraffe's
 * own Edit Properties panel, its filters and its exports can read it — and
 * `{"gtablesProps": {"a": 1}}` is a value only this app can see.
 *
 * `updateBlock` merges TOP-LEVEL fields only — `properties` is replaced
 * wholesale — so the current properties are read-merged here. `managed` names
 * the columns the sheet controls: one cleared out of a column has to be
 * DELETED, and collecting only what is filled in is how a deletion gets lost.
 */
export async function writeBlockProps(
  blockId: string,
  current: Record<string, unknown> | undefined,
  values: Record<string, unknown>,
  managed: Iterable<string>
): Promise<{ ok: boolean; reason?: string }> {
  const properties = { ...(current ?? {}) };
  for (const field of managed) delete properties[field];
  Object.assign(properties, values);
  const res = await invoke('updateBlock', [blockId, { properties }], 30000);
  return res.ok ? { ok: true } : { ok: false, reason: summarise(res.reason) };
}

export async function renameBlock(blockId: string, name: string): Promise<{ ok: boolean; reason?: string }> {
  // The name IS a top-level field, so unlike `properties` it needs no
  // read-merge. The one case where sending the bare field is right.
  const res = await invoke('updateBlock', [blockId, { name }], 30000);
  return res.ok ? { ok: true } : { ok: false, reason: summarise(res.reason) };
}

/**
 * Hide features on the map.
 *
 * `setSdkRender` is a complete override set, not a merge: `hideFeatureIds` goes
 * on EVERY call, empty array included. Omit it and the host keeps the previous
 * hide list, so a row switched back on stays invisible. Passing nothing at all
 * hands rendering back to the project.
 */
export async function setHiddenFeatures(ids: readonly string[]): Promise<boolean> {
  const res = await invoke('setSdkRender', [{ hideFeatureIds: [...ids], features: {} }], 10000);
  return res.ok;
}

export async function releaseRender(): Promise<boolean> {
  const res = await invoke('setSdkRender', [null], 10000);
  return res.ok;
}
